import React, { useState, useEffect, useCallback } from 'react';
import SafetyNote from './SafetyNote';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Button,
  useTheme,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { ArrowBack, Schedule, Map, Edit, Save, Cancel, ContentCopy, Description, Delete, Refresh, People, DynamicFeed } from '@mui/icons-material';
import { useBuddies } from '../hooks/useBuddies';
import ShareDiveDialog from './ShareDiveDialog';
import PostComposer from './PostComposer';
import { authService } from '../services/authService';
import { Dive } from '../types/Dive';
import type { AdditionalDiveData } from '../types/Dive';
import { diveService } from '../services/api';
import {
  UnitPreferences,
  PressureUnit,
  formatDepth,
  formatTemperature,
  psiToBar,
  barToPsi,
  metersToFeet,
  calculateDiveSAC,
  getDiveTankVolume,
  getGasConsumptionDisplayValue,
  formatGasConsumptionDisplayValue,
  getGasConsumptionMetricLabel,
  getGasConsumptionMetricTitle,
} from '../utils/unitConversions';
import { ENABLE_GAS_ANALYTICS } from '../config/featureFlags';
import { geocodingService } from '../services/geocodingService';
import { useDiveLocations } from '../hooks/useDiveLocations';
import { userDataService } from '../services/userDataService';
import { preferencesManager } from '../utils/preferencesManager';
import { OptionsManager, formatDateTime } from '../utils/optionsManager';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import DiveEntryForm from './DiveEntryForm';
import DiveProfileChart, { computeMaxAscentRate } from './DiveProfileChart';
import EquipmentSelector from './EquipmentSelector';
import NereusIcon from './NereusIcon';
import { getDiveDisplayName } from '../utils/diveDisplayName';
import { useNotification } from './NotificationContext';

// Create a custom dive site marker
const createDiveSiteIcon = (diveType: string): L.DivIcon => {
  const getIconColor = (type: string) => {
    switch (type) {
      case 'Scubadiving': return '#00ACC1';
      case 'Freediving': return '#26A69A';
      case 'Snorkeling': return '#66BB6A';
      case 'Spearfishing': return '#ff9800';
      case 'Mermaiding': return '#ba68c8';
      default: return '#0077BE';
    }
  };

  const color = getIconColor(diveType);

  // Removed emoji from SVG text to fix mobile rendering issues
  const svgIcon = `
    <svg width="30" height="46" viewBox="0 0 30 46" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C6.7 0 0 6.7 0 15C0 26.3 15 46 15 46S30 26.3 30 15C30 6.7 23.3 0 15 0Z" fill="${color}" stroke="white" stroke-width="2"/>
      <circle cx="15" cy="15" r="8" fill="white"/>
    </svg>
  `;

  return new L.DivIcon({
    html: svgIcon,
    className: 'custom-dive-site-marker',
    iconSize: [30, 46],
    iconAnchor: [15, 46],
    popupAnchor: [0, -46],
  } as L.DivIconOptions);
};

const DiveDetail: React.FC = () => {
  const { diveId } = useParams<{ diveId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showSuccess, showError, showWarning } = useNotification();
  const currentUserId = authService.getUser()?.userId ?? null;
  const { buddies } = useBuddies(currentUserId);
  const [dive, setDive] = useState<Dive | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailedData, setDetailedData] = useState<any>(null);
  const [detailedDataLoading, setDetailedDataLoading] = useState(false);
  const [unitPreferences, setUnitPreferences] = useState<UnitPreferences>(() => {
    const optionsManager = OptionsManager.getInstance();
    return optionsManager.getOption('units');
  });
  const optionsManager = OptionsManager.getInstance();
  const options = optionsManager.getOptions();
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<string | null>(null);
  const [locationDisplayPreference, setLocationDisplayPreference] = useState<'geotagged' | 'description'>('geotagged');
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [editedLocationName, setEditedLocationName] = useState('');
  const { getLocation, saveLocation, loading: locationsLoading } = useDiveLocations();

  // Additional dive data state
  const [additionalData, setAdditionalData] = useState<AdditionalDiveData>({});
  const [isEditingAdditionalData, setIsEditingAdditionalData] = useState(false);
  const [additionalDataLoading, setAdditionalDataLoading] = useState(false);

  // Diver notes state
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Refresh from Suunto state
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Edit dive dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Share-with-buddy dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [postComposerOpen, setPostComposerOpen] = useState(false);

  // Refresh key to trigger data reload
  const [refreshKey, setRefreshKey] = useState(0);
  const hasDiveCoordinates = typeof dive?.latitude === 'number' && typeof dive?.longitude === 'number';
  const canPersistDiveExtras = hasDiveCoordinates;

  const convertPressureValue = (value: number | undefined, fromUnit: 'bar' | 'psi', toUnit: 'bar' | 'psi') => {
    if (value === undefined || fromUnit === toUnit) {
      return value;
    }

    return fromUnit === 'bar' ? barToPsi(value) : psiToBar(value);
  };

  // Clear detailedData when diveId changes to prevent stale data
  useEffect(() => {
    setDetailedData(null);
    setDetailedDataLoading(false);
  }, [diveId]);

  // Initialize Leaflet icon defaults (mobile-safe)
  useEffect(() => {
    // Only run once on mount
    try {
      // Fix for default markers not showing up in React Leaflet
      const prototype = L.Icon.Default.prototype as any;
      if (prototype._getIconUrl) {
        delete prototype._getIconUrl;
      }
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      });
    } catch (error) {
      console.warn('Failed to initialize Leaflet icons:', error);
    }
  }, []);

  // Subscribe to options changes for unit preferences
  useEffect(() => {
    const optionsManager = OptionsManager.getInstance();
    const unsubscribe = optionsManager.subscribe((options) => {
      setUnitPreferences(options.units);
    });

    return unsubscribe;
  }, []);

  // Resolve the current userId. The in-memory preferencesManager userId is null
  // on a fresh page load (e.g. navigating directly to a dive), so fall back to the
  // persisted authenticated user and prime preferencesManager for subsequent calls.
  // Declared before the fetch effect below so it is initialized when that effect runs.
  const resolveUserId = useCallback((): string | null => {
    let userId = preferencesManager.getUserId();
    if (!userId) {
      const user = authService.getUser();
      if (user) {
        preferencesManager.setUserId(user.userId);
        userId = user.userId;
      }
    }
    return userId;
  }, []);

  useEffect(() => {
    const fetchDive = async () => {
      if (!diveId) return;

      const accessToken = localStorage.getItem('suunto_access_token');
      const jwtToken = localStorage.getItem('authToken');

      if (!accessToken && !jwtToken) {
        navigate('/');
        return;
      }

      // Initialize demo mode if using demo token
      if (accessToken === 'DEMO_MODE_TOKEN') {
        preferencesManager.initializeDemoMode();
      }

      try {
        setLoading(true);
        let diveData;

        // Demo mode serves dives locally — no backend auth involved
        if (accessToken === 'DEMO_MODE_TOKEN') {
          diveData = await diveService.getDive(diveId, accessToken);
        } else if (jwtToken) {
          const { diveService: jwtDiveService } = await import('../services/diveService');
          try {
            diveData = await jwtDiveService.getMyDive(diveId);
          } catch (jwtError) {
            if (accessToken && accessToken !== 'DEMO_MODE_TOKEN') {
              diveData = await diveService.getDive(diveId, accessToken);
            } else {
              throw jwtError;
            }
          }
        } else if (accessToken && accessToken !== 'DEMO_MODE_TOKEN') {
          // No JWT token, use Suunto token (legacy support)
          diveData = await diveService.getDive(diveId, accessToken);
        } else {
          throw new Error('No authentication available');
        }

        setDive(diveData);

        const baseAdditionalData: AdditionalDiveData = {
          ...diveData.additionalData,
          equipmentIds: diveData.additionalData?.equipmentIds !== undefined ? diveData.additionalData.equipmentIds : undefined,
        };
        
        // Load additional dive data if available
        if (diveData.latitude && diveData.longitude) {
          const userId = resolveUserId();
          if (userId) {
            const diveKey = userDataService.generateDiveKey(
              diveData.latitude,
              diveData.longitude,
              new Date(diveData.startTime)
            );
            const loadedAdditionalData = await userDataService.getAdditionalDiveData(userId, diveKey);
            setAdditionalData({
              ...baseAdditionalData,
              ...loadedAdditionalData,
              equipmentIds: loadedAdditionalData.equipmentIds !== undefined ? loadedAdditionalData.equipmentIds : baseAdditionalData.equipmentIds,
            });
          }
          else {
            setAdditionalData(baseAdditionalData);
          }
        } else {
          setAdditionalData(baseAdditionalData);
        }
        
        // Fetch detailed dive data (FIT file and SAC data) in the background
        // Only available for Suunto dives, not manually entered dives
        if (accessToken) {
          setDetailedDataLoading(true);

          try {
            const detailedDiveData = await diveService.getDetailedDiveData(diveId, accessToken);
            setDetailedData(detailedDiveData);
            setDetailedDataLoading(false);

            // Store in sessionStorage for quick access if navigating back
            sessionStorage.setItem(`detailedDiveData_${diveId}`, JSON.stringify(detailedDiveData));
          } catch (err) {
            console.error('Failed to fetch detailed dive data:', err);
            setDetailedDataLoading(false);
            // Continue without detailed data - component will still show basic dive info
          }
        } else {
          setDetailedDataLoading(false);
        }
      } catch (err) {
        console.error('Failed to load dive details:', err);
        const message = err instanceof Error ? err.message : 'Failed to load dive details';
        setError(message === 'Dive not found' ? 'Dive not found' : 'Failed to load dive details');
      } finally {
        setLoading(false);
      }
    };

    fetchDive();

    // Listen for detailed dive data events
    const handleDetailedDataReady = (event: CustomEvent) => {
      if (event.detail.diveId === diveId) {
        setDetailedData(event.detail.data);
        setDetailedDataLoading(false);
      }
    };

    window.addEventListener('detailedDiveDataReady', handleDetailedDataReady as EventListener);

    return () => {
      sessionStorage.removeItem(`detailedDiveData_${diveId}`);
      window.removeEventListener('detailedDiveDataReady', handleDetailedDataReady as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diveId, navigate, refreshKey]);

  // Subscribe to preference changes
  useEffect(() => {
    // Set initial preference
    const currentPref = preferencesManager.getPreferences()?.locationDisplayPreference || 'geotagged';
    setLocationDisplayPreference(currentPref);

    // Subscribe to changes
    const unsubscribe = preferencesManager.subscribe((prefs) => {
      setLocationDisplayPreference(prefs.locationDisplayPreference || 'geotagged');
    });

    return unsubscribe;
  }, []);

  // Initialize current location using the hierarchy with user preference
  useEffect(() => {
    if (dive) {
      let customLocation = undefined;

      if (dive.latitude && dive.longitude) {
        const diveKey = userDataService.generateDiveKey(
          dive.latitude,
          dive.longitude,
          new Date(dive.startTime)
        );
        customLocation = getLocation(diveKey, dive.latitude, dive.longitude);
      }

      // Use the hierarchy utility function
      const displayName = getDiveDisplayName(dive, customLocation, locationDisplayPreference);
      setCurrentLocation(displayName);
    }
  }, [dive, getLocation, locationDisplayPreference]);

  // Helper function to check if location is in GPS coordinate format
  const isGPSCoordinateFormat = (location: string) => {
    if (!location) return false;

    // Use the same simple detection logic as DiveCard
    const isCoordinateFormat = location.includes('°') && location.includes(',');

    return isCoordinateFormat;
  };

  // Auto-geocode location if it's still in GPS coordinate format (separate effect)
  useEffect(() => {
    const autoGeocode = async () => {
      if (!dive || !diveId || !currentLocation || isGeocodingLoading) return;
      if (!dive.latitude || !dive.longitude) return;

      // console.log('🔍 Checking if location needs geocoding:', currentLocation);
      
      // Check if location is still in GPS coordinate format and trigger geocoding
      if (isGPSCoordinateFormat(currentLocation)) {
        // console.log('🗺️ Auto-geocoding GPS coordinates on dive detail page:', currentLocation);
        setIsGeocodingLoading(true);
        
        try {
          // Use frontend geocoding service instead of backend API
          const location = await geocodingService.getLocationName(dive.latitude, dive.longitude);
          // console.log('📍 Auto-geocoding completed:', location);
          setCurrentLocation(location);
        } catch (error) {
          // console.error('❌ Auto-geocoding failed:', error);
        } finally {
          setIsGeocodingLoading(false);
        }
      }
    };

    // Small delay to ensure state is settled
    const timeoutId = setTimeout(autoGeocode, 100);
    return () => clearTimeout(timeoutId);
  }, [dive, diveId, currentLocation, isGeocodingLoading]); // This will only run when currentLocation changes

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!dive) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Dive not found
      </Alert>
    );
  }

  const formatDuration = (duration: string) => {
    const match = duration.match(/(\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      const [, hours, minutes, seconds] = match;
      return `${parseInt(hours)}h ${parseInt(minutes)}m ${parseInt(seconds)}s`;
    }
    return duration;
  };

  // Check if user is authenticated with JWT (manually created dives)
  const isJWTAuthenticated = () => {
    return !!localStorage.getItem('authToken');
  };

  // Check if dive is from Suunto (not manually created or imported from a file)
  const isSuuntoDive = () => {
    return dive && dive.activityId &&
      !dive.activityId.startsWith('manual-') &&
      !dive.activityId.startsWith('import-');
  };

  // Handle delete dive
  const handleDeleteDive = async () => {
    if (!diveId) return;

    try {
      setDeleteLoading(true);
      const { diveService: jwtDiveService } = await import('../services/diveService');
      await jwtDiveService.deleteDive(diveId);
      setDeleteDialogOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Failed to delete dive:', error);
      showError('Failed to delete dive. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle refresh dive from Suunto
  const handleRefreshFromSuunto = async () => {
    if (!diveId) return;

    try {
      setRefreshing(true);
      setRefreshMessage(null);
      const { diveService: jwtDiveService } = await import('../services/diveService');
      const result = await jwtDiveService.refreshDiveFromSuunto(diveId);

      setRefreshMessage({
        type: 'success',
        text: result.message || 'Dive refreshed successfully from Suunto'
      });

      // Reload dive data in place rather than navigating away
      setRefreshKey(prev => prev + 1);
    } catch (error: any) {
      console.error('Failed to refresh dive from Suunto:', error);
      setRefreshMessage({
        type: 'error',
        text: error.message || 'Failed to refresh dive from Suunto. Please try again.'
      });
    } finally {
      setRefreshing(false);
    }
  };

  const displayWeightUnit = additionalData.weightUnit || options.units.weight;
  const displayPressureUnit = additionalData.pressureUnit || options.units.pressure;
  const selectedTankSize = additionalData.tankSize || options.equipment.tankSize;
  const derivedPressureConsumed =
    additionalData.startPressure !== undefined &&
    additionalData.endPressure !== undefined &&
    additionalData.startPressure > additionalData.endPressure
      ? additionalData.startPressure - additionalData.endPressure
      : additionalData.airPressureConsumed;

  const pressurePresets = displayPressureUnit === 'psi'
    ? [3000, 3200, 3442]
    : [200, 207, 232];

  const depthReadings = detailedData?.fitFileData?.depthData?.depthReadings;
  const temperatureReadings = detailedData?.fitFileData?.temperatureData?.temperatureReadings;
  const sacRate = detailedData?.sacData?.sacRate;

  // App-computed gas consumption: pressure-derived RMV or the directly entered value,
  // shown in the user's preferred metric (falls back to RMV when SAC needs a missing tank volume)
  const appRmv = calculateDiveSAC({ ...dive, additionalData });
  let gasMetricUsed = options.display.gasConsumptionMetric;
  let gasDisplayValue = appRmv !== null
    ? getGasConsumptionDisplayValue(
        appRmv,
        gasMetricUsed,
        displayPressureUnit as PressureUnit,
        getDiveTankVolume({ additionalData }) ?? undefined
      )
    : null;
  if (appRmv !== null && gasDisplayValue === null) {
    gasMetricUsed = 'rmv';
    gasDisplayValue = getGasConsumptionDisplayValue(appRmv, 'rmv', displayPressureUnit as PressureUnit);
  }
  const gasConsumptionDisplay = gasDisplayValue !== null
    ? formatGasConsumptionDisplayValue(gasDisplayValue, gasMetricUsed, displayPressureUnit as PressureUnit)
    : null;

  // Compose a dive summary and hand it to Nereus for a post-dive debrief
  const handleAskNereus = () => {
    const useFeet = unitPreferences.depth === 'feet';
    const facts: string[] = [
      `max depth ${formatDepth(dive.maxDepth, unitPreferences.depth)}`,
      `avg depth ${formatDepth(dive.avgDepth, unitPreferences.depth)}`,
      `duration ${formatDuration(dive.duration)}`,
      `water temp ${formatTemperature(dive.waterTemperature, unitPreferences.temperature)}`,
    ];
    const gasType = detailedData?.fitFileData?.diveSettings?.gasType;
    if (gasType) facts.push(`breathing ${gasType}`);
    if (additionalData.startPressure && additionalData.endPressure) {
      facts.push(`tank pressure ${additionalData.startPressure} → ${additionalData.endPressure} ${displayPressureUnit}`);
    }
    if (gasConsumptionDisplay) {
      facts.push(`${getGasConsumptionMetricLabel(gasMetricUsed)} ${gasConsumptionDisplay}`);
    }
    if (sacRate) facts.push(`estimated SAC rate ${sacRate}`);
    if (Array.isArray(depthReadings) && depthReadings.length > 1) {
      const rate = computeMaxAscentRate(depthReadings);
      if (rate > 0) {
        facts.push(`max ascent rate ${(useFeet ? metersToFeet(rate) : rate).toFixed(1)} ${useFeet ? 'ft' : 'm'}/min`);
      }
    }
    if (additionalData.suit?.type && additionalData.suit.type !== 'none') {
      facts.push(
        additionalData.suit.type === 'wetsuit' && additionalData.suit.thicknessMm
          ? `${additionalData.suit.thicknessMm}mm wetsuit`
          : additionalData.suit.type
      );
    }

    const where = currentLocation || dive.location;
    const when = formatDateTime(new Date(dive.startTime), options.display.timeFormat, options.display.dateFormat, dive.latitude, dive.longitude);
    const message =
      `Here's my ${dive.diveType} dive from ${when}${where ? ` at ${where}` : ''}: ${facts.join(', ')}.` +
      (additionalData.notes ? ` My notes: "${additionalData.notes}"` : '') +
      ' Can you give me a quick debrief — what went well, and what should I work on next?';

    navigate('/chat', { state: { initialMessage: message } });
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        {!isEditingLocation ? (
          <>
            {/* A long site name must not squeeze the action buttons off-screen on
                narrow viewports — take the full row and let the buttons wrap below. */}
            <Typography
              variant="h4"
              sx={{
                flex: { xs: '1 1 100%', sm: 1 },
                minWidth: 0,
                overflowWrap: 'anywhere',
              }}
            >
              {currentLocation || dive.location || 'Dive Details'}
            </Typography>
            {isGeocodingLoading && (
              <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="textSecondary">
                  Finding location...
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, ml: { xs: 0, sm: 2 }, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => {
                  setIsEditingLocation(true);
                  setEditedLocationName(currentLocation || dive.location || '');
                }}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {dive.latitude && dive.longitude ? 'Edit Location Name' : 'Edit Dive Name'}
              </Button>
              {isJWTAuthenticated() && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => setEditDialogOpen(true)}
                  color="primary"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Edit Dive Details
                </Button>
              )}
              {isJWTAuthenticated() && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<People />}
                  onClick={() => setShareDialogOpen(true)}
                  color="primary"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Share with Buddy
                </Button>
              )}
              {isJWTAuthenticated() && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DynamicFeed />}
                  onClick={() => setPostComposerOpen(true)}
                  color="primary"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Post to Feed
                </Button>
              )}
            </Box>
            {isJWTAuthenticated() && isSuuntoDive() && (
              <IconButton
                size="small"
                onClick={handleRefreshFromSuunto}
                title="Refresh dive data from Suunto"
                sx={{ ml: 1 }}
                color="primary"
                disabled={refreshing}
              >
                {refreshing ? <CircularProgress size={20} /> : <Refresh fontSize="small" />}
              </IconButton>
            )}
            {isJWTAuthenticated() && (
              <IconButton
                size="small"
                onClick={() => setDeleteDialogOpen(true)}
                title="Delete dive"
                sx={{ ml: 1 }}
                color="error"
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </>
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              label={dive.latitude && dive.longitude ? "Location Name" : "Dive Name"}
              value={editedLocationName}
              onChange={(e) => setEditedLocationName(e.target.value)}
              sx={{ flex: 1, mr: 2 }}
              helperText={dive.latitude && dive.longitude
                ? "Applied to all dives at this location"
                : "Custom name for this specific dive"}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                color="primary"
                onClick={async () => {
                  try {
                    const userId = resolveUserId();
                    if (!userId) {
                      showWarning('Please set up your user account first');
                      return;
                    }

                    if (dive.latitude && dive.longitude) {
                      await saveLocation(
                        dive.latitude,
                        dive.longitude,
                        new Date(dive.startTime),
                        editedLocationName,
                        undefined,
                        true
                      );
                      setCurrentLocation(editedLocationName);
                      setIsEditingLocation(false);
                      showSuccess('Location name saved! This will apply to all dives at this location.');
                    } else {
                      const { diveService: jwtDiveService } = await import('../services/diveService');
                      const response = await jwtDiveService.updateDive(dive.id, {
                        diveName: editedLocationName
                      });

                      if (response.success) {
                        setDive(response.dive);
                        setCurrentLocation(editedLocationName);
                        setIsEditingLocation(false);
                        showSuccess('Dive name saved successfully!');
                      }
                    }
                  } catch (error) {
                    console.error('Failed to save:', error);
                    showError('Failed to save. Please try again.');
                  }
                }}
                disabled={locationsLoading || !editedLocationName.trim()}
                title="Save"
              >
                {locationsLoading ? <CircularProgress size={20} /> : <Save />}
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  setIsEditingLocation(false);
                  setEditedLocationName('');
                }}
                title="Cancel"
              >
                <Cancel />
              </IconButton>
            </Box>
          </>
        )}
        <Chip
          label={dive.diveType}
          size="medium"
          color="primary"
          variant="outlined"
          sx={{ ml: 2 }}
        />
      </Box>

      {/* Refresh Message */}
      {refreshMessage && (
        <Alert
          severity={refreshMessage.type}
          sx={{ mb: 2 }}
          onClose={() => setRefreshMessage(null)}
        >
          {refreshMessage.text}
        </Alert>
      )}

      {/* Provenance for a dive that came from a buddy's share */}
      {dive.sharedFromUserId && (
        <Alert severity="info" icon={<People fontSize="small" />} sx={{ mb: 2 }}>
          This dive came from a buddy's share. The profile and conditions are theirs — your air,
          weights and gear are yours to fill in.
        </Alert>
      )}

      {/* Dive Information Section */}
      {dive && (
        <Box mt={3}>
          <Paper elevation={2} sx={{ p: 4 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={3}
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                <Schedule color="primary" />
                Dive Information
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  startIcon={<NereusIcon fontSize="small" />}
                  onClick={handleAskNereus}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Ask Nereus about this dive
                </Button>
                {!isEditingAdditionalData ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    disabled={!canPersistDiveExtras}
                    onClick={() => setIsEditingAdditionalData(true)}
                  >
                    Edit
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={additionalDataLoading ? <CircularProgress size={16} /> : <Save />}
                      disabled={additionalDataLoading}
                      onClick={async () => {
                        try {
                          setAdditionalDataLoading(true);
                          if (!canPersistDiveExtras) {
                            showWarning('This dive does not have GPS coordinates, so extra dive details cannot be saved yet.');
                            return;
                          }
                          const userId = resolveUserId();
                          if (!userId) {
                            showWarning('Please set up your user account first');
                            return;
                          }
                          const diveKey = userDataService.generateDiveKey(
                            dive.latitude!,
                            dive.longitude!,
                            new Date(dive.startTime)
                          );
                          const normalizedAdditionalData = {
                            ...additionalData,
                            airPressureConsumed:
                              additionalData.startPressure !== undefined &&
                              additionalData.endPressure !== undefined &&
                              additionalData.startPressure > additionalData.endPressure
                                ? additionalData.startPressure - additionalData.endPressure
                                : additionalData.airPressureConsumed,
                          };
                          await userDataService.saveAdditionalDiveData(userId, diveKey, normalizedAdditionalData);
                          setAdditionalData(normalizedAdditionalData);
                          setIsEditingAdditionalData(false);
                        } catch (error) {
                          console.error('Failed to save additional data:', error);
                          showError('Failed to save additional dive data');
                        } finally {
                          setAdditionalDataLoading(false);
                        }
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Cancel />}
                      onClick={() => setIsEditingAdditionalData(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            {!canPersistDiveExtras && (
              <Alert severity="info" sx={{ mb: 3 }}>
                This dive has no GPS coordinates from Suunto. Core dive details are still shown, but extra annotations cannot be saved until a stable dive key is available.
              </Alert>
            )}
            
            {/* Quick Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={6} md={3}>
                <Box sx={{
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: isDark ? '#1a2b3d' : '#e3f2fd',
                  borderRadius: 2,
                  border: isDark ? '1px solid #42a5f5' : '1px solid #1976d2'
                }}>
                  <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {formatDepth(dive.maxDepth, unitPreferences.depth)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                    Max Depth
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} md={3}>
                <Box sx={{
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: isDark ? '#332614' : '#fff3e0',
                  borderRadius: 2,
                  border: isDark ? '1px solid #ffa726' : '1px solid #ff9800'
                }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 1 }}>
                    {formatDepth(dive.avgDepth, unitPreferences.depth)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                    Avg Depth
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} md={3}>
                <Box sx={{
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: isDark ? '#2d1b33' : '#f3e5f5',
                  borderRadius: 2,
                  border: isDark ? '1px solid #ba68c8' : '1px solid #9c27b0'
                }}>
                  <Typography variant="h3" color="secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {formatDuration(dive.duration).replace(/h|m|s/g, '').replace(/\s+/g, ':')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                    Duration
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} md={3}>
                <Box sx={{
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: isDark ? '#1a2e1a' : '#e8f5e8',
                  borderRadius: 2,
                  border: isDark ? '1px solid #66bb6a' : '1px solid #4caf50'
                }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 1 }}>
                    {formatTemperature(dive.waterTemperature, unitPreferences.temperature)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                    Temperature
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Detailed Information */}
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'text.secondary' }}>
              Details
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Date & Time
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatDateTime(new Date(dive.startTime), options.display.timeFormat, options.display.dateFormat, dive.latitude, dive.longitude)}
                  </Typography>
                </Box>
              </Grid>

              {detailedData?.fitFileData?.diveSettings?.gasType && (
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Gas Type
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {detailedData?.fitFileData?.diveSettings?.gasType}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {ENABLE_GAS_ANALYTICS && gasConsumptionDisplay && (
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      title={getGasConsumptionMetricTitle(gasMetricUsed)}
                      sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      {getGasConsumptionMetricLabel(gasMetricUsed)}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {gasConsumptionDisplay}
                    </Typography>
                    <SafetyNote kind="gasEstimate" sx={{ mt: 0.25 }} />
                  </Box>
                </Grid>
              )}

              {ENABLE_GAS_ANALYTICS && sacRate && (
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      SAC Rate (est.)
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {sacRate}
                    </Typography>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={3}>
                {isEditingAdditionalData ? (
                  <TextField
                    fullWidth
                    label="Weight Used"
                    type="number"
                    value={additionalData.weightUsed || ''}
                    onChange={(e) => setAdditionalData({
                      ...additionalData,
                      weightUsed: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {displayWeightUnit}
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Weight Used
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {additionalData.weightUsed
                        ? `${additionalData.weightUsed} ${displayWeightUnit}`
                        : '—'}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                {isEditingAdditionalData ? (
                  <FormControl fullWidth>
                    <InputLabel>Pressure Unit</InputLabel>
                    <Select
                      value={displayPressureUnit}
                      label="Pressure Unit"
                      onChange={(e) => {
                        const nextUnit = e.target.value as 'bar' | 'psi';
                        const previousUnit = displayPressureUnit;

                        setAdditionalData({
                          ...additionalData,
                          pressureUnit: nextUnit,
                          airPressureConsumed: convertPressureValue(additionalData.airPressureConsumed, previousUnit, nextUnit),
                          startPressure: convertPressureValue(additionalData.startPressure, previousUnit, nextUnit),
                          endPressure: convertPressureValue(additionalData.endPressure, previousUnit, nextUnit),
                        });
                      }}
                    >
                      <MenuItem value="bar">bar</MenuItem>
                      <MenuItem value="psi">psi</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Pressure Used
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {derivedPressureConsumed
                        ? `${derivedPressureConsumed} ${displayPressureUnit}`
                        : '—'}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                {isEditingAdditionalData ? (
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>Tank Size</InputLabel>
                      <Select
                        value={selectedTankSize}
                        label="Tank Size"
                        onChange={(e) => setAdditionalData({
                          ...additionalData,
                          tankSize: e.target.value as any,
                          customTankVolume: e.target.value === 'custom' ? additionalData.customTankVolume : undefined
                        })}
                      >
                        <MenuItem value="AL80">AL80 (11.0L)</MenuItem>
                        <MenuItem value="AL63">AL63 (8.5L)</MenuItem>
                        <MenuItem value="AL100">AL100 (13.2L)</MenuItem>
                        <MenuItem value="Steel72">Steel 72 (9.6L)</MenuItem>
                        <MenuItem value="Steel100">Steel 100 (12.0L)</MenuItem>
                        <MenuItem value="Steel120">Steel 120 (15.1L)</MenuItem>
                        <MenuItem value="Steel130">Steel 130 (16.4L)</MenuItem>
                        <MenuItem value="custom">Custom</MenuItem>
                      </Select>
                    </FormControl>
                    {additionalData.tankSize === 'custom' && (
                      <TextField
                        fullWidth
                        label="Volume"
                        type="number"
                        value={additionalData.customTankVolume || ''}
                        onChange={(e) => setAdditionalData({
                          ...additionalData,
                          customTankVolume: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">L</InputAdornment>,
                        }}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Tank Size
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {additionalData.tankSize === 'custom' && additionalData.customTankVolume
                        ? `Custom (${additionalData.customTankVolume}L)`
                        : additionalData.tankSize
                        ? additionalData.tankSize
                        : '—'}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                {isEditingAdditionalData ? (
                  <TextField
                    fullWidth
                    label="Start Pressure"
                    type="number"
                    value={additionalData.startPressure || ''}
                    onChange={(e) => setAdditionalData({
                      ...additionalData,
                      startPressure: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{displayPressureUnit}</InputAdornment>,
                    }}
                    placeholder={displayPressureUnit === 'psi' ? 'e.g. 3000' : 'e.g. 200'}
                    helperText="Tank pressure at start"
                  />
                ) : (
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Start Pressure
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {additionalData.startPressure
                        ? `${additionalData.startPressure} ${displayPressureUnit}`
                        : '—'}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                {isEditingAdditionalData ? (
                  <TextField
                    fullWidth
                    label="End Pressure"
                    type="number"
                    value={additionalData.endPressure || ''}
                    onChange={(e) => setAdditionalData({
                      ...additionalData,
                      endPressure: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{displayPressureUnit}</InputAdornment>,
                    }}
                    placeholder={displayPressureUnit === 'psi' ? 'e.g. 700' : 'e.g. 50'}
                    helperText={derivedPressureConsumed ? `Pressure used: ${derivedPressureConsumed.toFixed(0)} ${displayPressureUnit}` : 'Tank pressure at end'}
                  />
                ) : (
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      End Pressure
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {additionalData.endPressure
                        ? `${additionalData.endPressure} ${displayPressureUnit}`
                        : '—'}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {isEditingAdditionalData && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="caption" color="textSecondary">
                      Quick fill:
                    </Typography>
                    {pressurePresets.map((preset) => (
                      <Button
                        key={preset}
                        size="small"
                        variant="outlined"
                        onClick={() => setAdditionalData({
                          ...additionalData,
                          startPressure: preset
                        })}
                      >
                        {preset} {displayPressureUnit}
                      </Button>
                    ))}
                    <Button
                      size="small"
                      onClick={() => setAdditionalData({
                        ...additionalData,
                        startPressure: undefined,
                        endPressure: undefined,
                        airPressureConsumed: undefined,
                      })}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={3}>
                {isEditingAdditionalData ? (
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>Thermal Suit</InputLabel>
                      <Select
                        value={additionalData.suit?.type || 'none'}
                        label="Thermal Suit"
                        onChange={(e) => setAdditionalData({
                          ...additionalData,
                          suit: {
                            type: e.target.value as 'none' | 'wetsuit' | 'drysuit',
                            thicknessMm: e.target.value === 'wetsuit' ? additionalData.suit?.thicknessMm : undefined
                          }
                        })}
                      >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="wetsuit">Wetsuit</MenuItem>
                        <MenuItem value="drysuit">Drysuit</MenuItem>
                      </Select>
                    </FormControl>
                    {additionalData.suit?.type === 'wetsuit' && (
                      <TextField
                        fullWidth
                        label="Thickness"
                        type="number"
                        value={additionalData.suit?.thicknessMm || ''}
                        onChange={(e) => setAdditionalData({
                          ...additionalData,
                          suit: {
                            ...additionalData.suit!,
                            thicknessMm: e.target.value ? parseFloat(e.target.value) : undefined
                          }
                        })}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                        }}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Thermal Suit
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {additionalData.suit?.type === 'wetsuit' && additionalData.suit.thicknessMm
                        ? `Wetsuit (${additionalData.suit.thicknessMm}mm)`
                        : additionalData.suit?.type === 'drysuit'
                        ? 'Drysuit'
                        : additionalData.suit?.type === 'none'
                        ? 'None'
                        : '—'}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                {isEditingAdditionalData ? (
                  <FormControl fullWidth>
                    <InputLabel>Temperature Comfort</InputLabel>
                    <Select
                      value={additionalData.temperatureComfort || ''}
                      label="Temperature Comfort"
                      onChange={(e) => setAdditionalData({
                        ...additionalData,
                        temperatureComfort: e.target.value as 'cold' | 'fine' | 'warm' | undefined
                      })}
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value="cold">❄️ Cold</MenuItem>
                      <MenuItem value="fine">👌 Fine</MenuItem>
                      <MenuItem value="warm">🔥 Warm</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Temperature Comfort
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {additionalData.temperatureComfort === 'cold' && '❄️ Cold'}
                      {additionalData.temperatureComfort === 'fine' && '👌 Fine'}
                      {additionalData.temperatureComfort === 'warm' && '🔥 Warm'}
                      {!additionalData.temperatureComfort && '—'}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                {isEditingAdditionalData ? (
                  <TextField
                    fullWidth
                    label="Dive Center"
                    value={additionalData.diveCenter || ''}
                    onChange={(e) => setAdditionalData({
                      ...additionalData,
                      diveCenter: e.target.value || undefined
                    })}
                    placeholder="Enter dive center name"
                  />
                ) : (
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Dive Center
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {additionalData.diveCenter || '—'}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {hasDiveCoordinates && (
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Coordinates
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body1">
                        {dive.latitude!.toFixed(6)}, {dive.longitude!.toFixed(6)}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Copy coordinates"
                        title="Copy coordinates"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(`${dive.latitude!.toFixed(6)}, ${dive.longitude!.toFixed(6)}`);
                            showSuccess('Coordinates copied to clipboard');
                          } catch {
                            showError('Failed to copy coordinates');
                          }
                        }}
                      >
                        <ContentCopy sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              )}

            </Grid>
          </Paper>
        </Box>
      )}

      {/* Dive Profile Chart (from FIT file depth samples) */}
      {Array.isArray(depthReadings) && depthReadings.length > 1 && (
        <DiveProfileChart
          depthReadings={depthReadings}
          temperatureReadings={Array.isArray(temperatureReadings) ? temperatureReadings : []}
          unitPreferences={unitPreferences}
        />
      )}

      {/* Suunto Description Section */}
      {dive && dive.description && (
        <Box mt={3}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Description color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Dive Description from Suunto
              </Typography>
              <Chip
                label="From Suunto App"
                size="small"
                color="info"
                variant="outlined"
              />
            </Box>
            <Box sx={{
              mt: 2,
              p: 2,
              backgroundColor: isDark ? 'rgba(33, 150, 243, 0.08)' : 'rgba(33, 150, 243, 0.05)',
              borderRadius: 1,
              border: isDark ? '1px solid rgba(33, 150, 243, 0.3)' : '1px solid rgba(33, 150, 243, 0.2)'
            }}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {dive.description}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Equipment Section */}
      {dive && (
        <Box mt={3}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                🎽 Equipment Used
              </Typography>
            </Box>
            {!canPersistDiveExtras && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Equipment can be viewed here, but changes cannot be saved for dives without GPS coordinates yet.
              </Alert>
            )}
            <EquipmentSelector
              selectedEquipmentIds={additionalData.equipmentIds || []}
              onEquipmentChange={(equipmentIds) => {
                setAdditionalData({
                  ...additionalData,
                  equipmentIds
                });
              }}
              onSave={async () => {
                try {
                  setAdditionalDataLoading(true);
                  if (!canPersistDiveExtras) {
                    showWarning('This dive does not have GPS coordinates, so equipment changes cannot be saved yet.');
                    return;
                  }
                  const userId = resolveUserId();
                  if (!userId) {
                    showWarning('Please set up your user account first');
                    return;
                  }
                  const diveKey = userDataService.generateDiveKey(
                    dive.latitude!,
                    dive.longitude!,
                    new Date(dive.startTime)
                  );
                  await userDataService.saveAdditionalDiveData(userId, diveKey, additionalData);
                } catch (error) {
                  console.error('Failed to save equipment:', error);
                  showError('Failed to save equipment. Please try again.');
                } finally {
                  setAdditionalDataLoading(false);
                }
              }}
              isSaving={additionalDataLoading}
            />
          </Paper>
        </Box>
      )}

      {/* Satellite Map Section */}
      {dive && dive.latitude && dive.longitude && (
        <Box mt={3}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Map color="primary" />
              <Typography variant="h6">
                Dive Site Location
              </Typography>
            </Box>
            <Box sx={{ height: 300, borderRadius: 2, overflow: 'hidden' }}>
              <MapContainer
                style={{ height: '100%', width: '100%' }}
                zoom={12}
                center={[dive.latitude, dive.longitude]}
                scrollWheelZoom={true}
              >
                {/* Satellite imagery tile layer */}
                <TileLayer
                  attribution='&copy; <a href="https://www.esri.com/en-us/home">Esri</a> | &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />

                {/* Street overlay for reference (optional, with reduced opacity) */}
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  opacity={0.3}
                />

                <Marker
                  position={[dive.latitude, dive.longitude]}
                  icon={createDiveSiteIcon(dive.diveType)}
                >
                  <Popup>
                    <Box sx={{ minWidth: { xs: 'min(250px, 70vw)', sm: 250 } }}>
                      <Typography variant="h6" gutterBottom>
                        {currentLocation || dive.location || 'Dive Site'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {formatDateTime(new Date(dive.startTime), options.display.timeFormat, options.display.dateFormat, dive.latitude, dive.longitude)}
                      </Typography>

                      <Divider sx={{ my: 1 }} />

                      <Typography variant="body2">
                        <strong>Type:</strong> {dive.diveType}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Max Depth:</strong> {formatDepth(dive.maxDepth, unitPreferences.depth)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Duration:</strong> {formatDuration(dive.duration)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Temperature:</strong> {formatTemperature(dive.waterTemperature, unitPreferences.temperature)}
                      </Typography>

                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                        📍 {dive.latitude.toFixed(6)}, {dive.longitude.toFixed(6)}
                      </Typography>
                    </Box>
                  </Popup>
                </Marker>
              </MapContainer>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Diver Notes Section */}
      {dive && (
        <Box mt={3}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Description color="primary" />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Diver Notes
                </Typography>
              </Box>
              <Box>
                {!isEditingNotes ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    disabled={!canPersistDiveExtras}
                    onClick={() => setIsEditingNotes(true)}
                  >
                    Edit
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={notesLoading ? <CircularProgress size={16} /> : <Save />}
                      disabled={notesLoading}
                      onClick={async () => {
                        try {
                          setNotesLoading(true);
                          if (!canPersistDiveExtras) {
                            showWarning('This dive does not have GPS coordinates, so notes cannot be saved yet.');
                            return;
                          }
                          const userId = resolveUserId();
                          if (!userId) {
                            showWarning('Please set up your user account first');
                            return;
                          }
                          const diveKey = userDataService.generateDiveKey(
                            dive.latitude!,
                            dive.longitude!,
                            new Date(dive.startTime)
                          );
                          await userDataService.saveAdditionalDiveData(userId, diveKey, additionalData);
                          setIsEditingNotes(false);
                        } catch (error) {
                          console.error('Failed to save notes:', error);
                          showError('Failed to save notes');
                        } finally {
                          setNotesLoading(false);
                        }
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Cancel />}
                      onClick={() => setIsEditingNotes(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            {!canPersistDiveExtras && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Notes are visible here, but edits cannot be saved for dives without GPS coordinates yet.
              </Alert>
            )}

            {isEditingNotes ? (
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Your dive notes"
                placeholder="Add notes about this dive - conditions, sightings, buddies, equipment, lessons learned, etc."
                value={additionalData.notes || ''}
                onChange={(e) => setAdditionalData({
                  ...additionalData,
                  notes: e.target.value || undefined
                })}
                sx={{ mt: 2 }}
              />
            ) : (
              <Box sx={{
                mt: 2,
                p: 2,
                minHeight: 100,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                borderRadius: 1,
                border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
              }}>
                {additionalData.notes ? (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {additionalData.notes}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    No notes added yet. Click Edit to add your dive notes.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Dive Buddies Section */}
      {buddies.length > 0 && (
        <Box mt={3}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
              <People color="primary" /> Dive Buddies
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your current dive buddies — use the Buddies page to manage connections.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {buddies.map(b => (
                <Chip
                  key={b.buddyUserId}
                  avatar={
                    <Avatar sx={{ bgcolor: 'primary.main', fontSize: '0.65rem' }}>
                      {b.buddyUserId.slice(0, 1).toUpperCase()}
                    </Avatar>
                  }
                  label={b.buddyUserId}
                  variant="outlined"
                  size="medium"
                />
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Loading indicator for detailed data */}
      {detailedDataLoading && (
        <Box mt={3} display="flex" alignItems="center" gap={2}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">
            Loading detailed dive data...
          </Typography>
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Dive?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this dive? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDive}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : undefined}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dive dialog */}
      <DiveEntryForm
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={() => {
          setEditDialogOpen(false);
          // Trigger dive data reload
          setRefreshKey(prev => prev + 1);
        }}
        diveToEdit={dive}
        editMode={true}
      />

      {/* Share with buddy dialog */}
      <ShareDiveDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        diveId={dive.id}
        diveLabel={`${currentLocation || dive.location || 'Dive'} · ${new Date(dive.startTime).toLocaleDateString()}`}
        currentUserId={currentUserId}
      />

      <PostComposer
        open={postComposerOpen}
        onClose={() => setPostComposerOpen(false)}
        onCreated={() => showSuccess('Posted to your feed!')}
        initialDiveId={dive.id}
      />
    </Box>
  );
};

export default DiveDetail;